import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    Form,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TextInput,
} from '@ksyos/design-system';
import { Link } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import type { TicketType } from '../../../../common/enums/ticketType';
import type { IPatient } from '../../../../common/interfaces/patient';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { getJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { DateInput } from '../../elements/common';
import { DisplayFullName } from '../../elements/DisplayFullName';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDatetime } from '../../elements/NullableFormattedDatetime';
import { DatePeriodSelection } from '../processes/adminProcessesList/header/DatePeriodSelection';

interface IPatientAccess {
    created_at: string;
    id: string;
    patient: Partial<IPatient>;
    practitioner: {
        id: string;
        nameFamily: string;
        nameGiven: string | null;
        nameInfix: string | null;
    };
    reasonForAccess: string;
    request: { processId: string | null };
    ticketInformation: string;
    ticketType: TicketType;
}

const itemsPerPage = 100;

export function PatientAccessList() {
    const [patientAccessList, setPatientAccessList] = useState<IPatientAccess[]>([]);
    const [total, setTotal] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [isSearching, setIsSearching] = useState(true);
    const [searchStartDate, setSearchStartDate] = useState<string | null>(null);
    const [searchEndDate, setSearchEndDate] = useState<string | null>(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);

    useEffect(() => {
        assertHasAuthorization(ManagerAuthorization.patientAccessLog);
        search();
    }, [currentPageNumber]);
    const canReadPractitioners = hasOneAuthorization(ManagerAuthorization.practitionerRead);

    return (
        <PageTemplate>
            <PageHeader title={translateKey('patientAccess.patientAccessLog')} />
            <Form
                onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    search();
                }}
                submitOnEnter={true}
            >
                <Box
                    alignItems="start"
                    display="grid"
                    gap={3}
                    gridTemplateColumns={[
                        '1fr',
                        null,
                        '1fr max-content 1fr',
                        '380px max-content max-content max-content',
                    ]}
                >
                    <TextInput
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={translateKey('practitioner.practitioner')}
                        value={searchText}
                    />
                    <DatePeriodSelection
                        endDate={
                            <DateInput
                                birthDate
                                error={
                                    searchStartDate !== null &&
                                    searchEndDate !== null &&
                                    !DateTime.from(searchEndDate).isSameOrAfter(searchStartDate)
                                        ? 'koi_endDate'
                                        : ''
                                }
                                hasMaxWidth={true}
                                onChange={(value) =>
                                    setSearchEndDate(value?.toLowerCase() !== 'invalid date' ? value : null)
                                }
                                placeholder={translateKey('common.dateFormat')}
                                size="large"
                                value={searchEndDate}
                                variant="outline"
                            />
                        }
                        startDate={
                            <DateInput
                                birthDate
                                hasMaxWidth={true}
                                onChange={(value) =>
                                    setSearchStartDate(value?.toLowerCase() !== 'invalid date' ? value : null)
                                }
                                placeholder={translateKey('common.dateFormat')}
                                size="large"
                                value={searchStartDate}
                                variant="outline"
                            />
                        }
                    />
                    <Button type="submit">{translateKey('common.search')}</Button>
                </Box>
            </Form>
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('common.date')}</TableHeader>
                            <TableHeader>{translateKey('practitioner.practitioner')}</TableHeader>
                            <TableHeader>{translateKey('patient.patient')}</TableHeader>
                            <TableHeader>{translateKey('common.namePatient')}</TableHeader>
                            <TableHeader>{translateKey('common.processId')}</TableHeader>
                            <TableHeader>{translateKey('patientAccess.reason')}</TableHeader>
                            <TableHeader>{translateKey('patientAccess.notification')}</TableHeader>
                            <TableHeader>{translateKey('patientAccess.notificationDetails')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {patientAccessList.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>
                                    <NullableFormattedDatetime value={item.created_at} />
                                </TableCell>
                                <TableCell>
                                    {canReadPractitioners ? (
                                        <Link to={`/practitioners/${item.practitioner.id}/profile`}>
                                            <DisplayFullName {...item.practitioner} />
                                        </Link>
                                    ) : (
                                        <DisplayFullName {...item.practitioner} />
                                    )}
                                </TableCell>
                                <TableCell>{item.patient.id}</TableCell>
                                <TableCell>{getDisplayName(item.patient)}</TableCell>
                                <TableCell>{item.request.processId}</TableCell>
                                <TableCell>{item.reasonForAccess}</TableCell>
                                <TableCell>
                                    {item.ticketType && translateKey(`ticketTypeEnum.${item.ticketType}`)}
                                </TableCell>
                                <TableCell>{item.ticketInformation}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ListPager
                isDisabled={isSearching}
                itemsPerPage={itemsPerPage}
                onChangePage={async (page: number) => setCurrentPageNumber(page)}
                page={currentPageNumber}
                total={total}
            />
        </PageTemplate>
    );

    async function search() {
        setIsSearching(true);

        let url = '/api/patientAccessReasons?';
        url += 'limit=' + itemsPerPage;
        url += '&offset=' + itemsPerPage * (currentPageNumber - 1);

        if (searchText) {
            url += `&searchText=${searchText}`;
        }

        if (searchStartDate) {
            url += `&searchStartDate=${searchStartDate}`;
        }

        if (searchEndDate) {
            url += `&searchEndDate=${searchEndDate}`;
        }

        const result = await getJson(url);
        if (result.json.items) {
            setTotal(result.json.total);
            setPatientAccessList(result.json.items);
        } else {
            setTotal(0);
            setPatientAccessList([]);
        }

        setIsSearching(false);
    }
}
